import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await login(email, password);
    setSubmitting(false);
    if (result.ok) {
      toast.success("Welcome back");
      navigate(next, { replace: true });
    } else {
      setError(result.error);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <div data-testid="login-page" className="grid lg:grid-cols-2 lg:min-h-[calc(100vh-4rem)]">
      <div className="hidden lg:block relative overflow-hidden border-r border-border">
        <img
          src="https://images.unsplash.com/photo-1669101283985-ab2b293923df?w=1400&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-foreground/85 via-foreground/55 to-foreground/20" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="overline text-white/70">{t("login.signedAccess")}</div>
          <p className="font-display text-3xl font-semibold mt-2 tracking-tight max-w-md drop-shadow-lg">
            {t("login.aside")}
          </p>
        </div>
      </div>

      <div className="px-6 lg:px-16 py-12 sm:py-16 flex items-start lg:items-center">
        <div className="max-w-md w-full mx-auto">
          <div className="overline mb-3">{t("nav.signin")}</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{t("login.title")}</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {t("login.subtitle")}
          </p>

          <Button
            type="button"
            variant="outline"
            className="w-full mt-8 rounded-md h-11"
            onClick={googleLogin}
            data-testid="google-login-button"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18a10.99 10.99 0 0 0 0 9.86l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            {t("login.google")}
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-border" />
            <span className="overline">{t("login.or")}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">{t("login.email")}</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password">{t("login.password")}</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-11"
                data-testid="login-password"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-destructive">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 rounded-md"
              data-testid="login-submit"
            >
              {submitting ? t("login.submitting") : t("login.submit")}
            </Button>
          </form>

          <div className="mt-8 text-sm text-muted-foreground space-y-2">
            <p>
              {t("login.newDoctor")}{" "}
              <Link to="/register" className="underline text-foreground">{t("login.registerLink")}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
